import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { Table, EmptyState, ButtonVariants, BadgeVariants } from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeSelection } from '@/hooks';
import { ConfirmDialog } from '@/components/feedback';
import { buildContractFilterDefinitions } from '@/config/filterDefinitions';
import { buildTableLabels } from '@/config/tableLabels';
import {
  SummaryTiles,
  DiscrepancyModal,
  LinkShipmentsForContract,
  buildContractLinkDetailFields,
} from '@/components/common';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { buildLinkedContractsColumns } from '../constants/incomingShipmentLinkedContractsColumns';
import { EMPTY_MAP } from '@/lib/utils';
import {
  useGetLinkedContractsForShipmentQuery,
  useUnlinkContractFromShipmentMutation,
} from '../api/incomingShipmentsApi';

const getLinkedContractId = (row) => row.id;
/**
 * Props:
 *   module          the active module route segment
 *   shipmentId      the shipment's database id
 *   shipment        the shipment detail view model
 *   onLinkContract  () => void — opens the link wizard
 */
export const LinkedContractsTabView = ({ module, shipmentId, shipment, onLinkContract }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const [removeLinkRow, setRemoveLinkRow] = useState(null);
  const [contractDiscrepancyRow, setContractDiscrepancyRow] = useState(null);
  const [linkShipmentRow, setLinkShipmentRow] = useState(null);
  const [filters, setFilters] = useState(EMPTY_MAP);

  // GraphQL: account-scoped, so unlike the unlink mutation it needs no `module`.
  const { data: linkedContracts } = useGetLinkedContractsForShipmentQuery({
    id: shipmentId,
    filters,
  });
  const [unlinkContractFromShipment] = useUnlinkContractFromShipmentMutation();

  const { selections: unitSelections, contextValue: unitTypeContext } = useUnitTypeSelection();
  const filterDefinitions = useMemo(() => buildContractFilterDefinitions(t), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const handleReceiveShipmentForContract = useCallback(
    (row) => {
      navigate(ROUTES.contractReceiveShipment(module, row.contractDbId));
    },
    [navigate, module],
  );
  const handleLinkShipmentForContract = useCallback((row) => setLinkShipmentRow(row), []);
  const handleCloseLinkShipment = useCallback(() => setLinkShipmentRow(null), []);

  const linkShipmentFields = useCallback(
    (unitType, assignedInUnit) =>
      buildContractLinkDetailFields(t, linkShipmentRow, shipment, unitType, assignedInUnit),
    [t, linkShipmentRow, shipment],
  );
  const handleFilterChange = useCallback((values) => setFilters(values), []);
  const handleRemoveLink = useCallback((row) => setRemoveLinkRow(row), []);
  const handleCancelRemoveLink = useCallback(() => setRemoveLinkRow(null), []);
  const handleConfirmRemoveLink = useCallback(async () => {
    await unlinkContractFromShipment({
      module,
      id: shipmentId,
      contractId: removeLinkRow.contractDbId,
    });
    setRemoveLinkRow(null);
  }, [unlinkContractFromShipment, module, shipmentId, removeLinkRow]);
  const handleOpenContractDiscrepancy = useCallback((row) => setContractDiscrepancyRow(row), []);
  const handleCloseContractDiscrepancy = useCallback(() => setContractDiscrepancyRow(null), []);

  const columns = useMemo(
    () => buildLinkedContractsColumns(t, { onOpenDiscrepancy: handleOpenContractDiscrepancy }),
    [t, handleOpenContractDiscrepancy],
  );

  const rowActions = useMemo(
    () => [
      {
        id: 'remove-link',
        label: t('INCOMING_SHIPMENTS_LINKED_CONTRACTS_ACTION_REMOVE_LINK'),
        variant: ButtonVariants.tertiary,
        onClick: handleRemoveLink,
      },
      {
        id: 'receive-shipment',
        label: t('ACTION_RECEIVE_SHIPMENT'),
        variant: ButtonVariants.tertiary,
        onClick: handleReceiveShipmentForContract,
      },
      {
        id: 'link-shipment',
        label: t('ACTION_LINK_SHIPMENT'),
        variant: ButtonVariants.tertiary,
        onClick: handleLinkShipmentForContract,
      },
    ],
    [t, handleRemoveLink, handleReceiveShipmentForContract, handleLinkShipmentForContract],
  );

  const contractDiscrepancies = contractDiscrepancyRow?.discrepancyItems ?? [];

  const contractDiscrepancySections = useMemo(() => {
    if (!contractDiscrepancyRow) return [];
    const selectedUnit =
      unitSelections.get(contractDiscrepancyRow.id) ?? contractDiscrepancyRow.unitType;
    const unitValues = contractDiscrepancyRow.byUnit?.[selectedUnit] ?? {};
    return [
      {
        title: t('SECTION_GENERAL_DETAILS'),
        fields: [
          {
            label: t('FIELD_CONTRACT_ID'),
            value: contractDiscrepancyRow.contractId,
          },
          {
            label: t('INCOMING_SHIPMENTS_LINKED_CONTRACTS_COL_LINKED_SHIPMENTS'),
            value: contractDiscrepancyRow.linkedShipments,
          },
          { label: t('FIELD_TERMINAL'), value: contractDiscrepancyRow.terminal },
          {
            label: t('FIELD_STATUS'),
            value: contractDiscrepancyRow.status,
            type: 'status',
            tone: BadgeVariants.success,
          },
        ],
      },
      {
        title: t('SECTION_QUANTITY'),
        fields: [
          {
            label: t('FIELD_ASSIGNED_QUANTITY_SHIPMENT'),
            value: unitValues.assignedQty,
          },
          {
            label: t('FIELD_CONTRACTED_QUANTITY'),
            value: unitValues.contractedQuantity,
          },
          {
            label: t('FIELD_OUTSTANDING_QUANTITY'),
            value: unitValues.outstandingQuantity,
          },
        ],
      },
    ];
  }, [contractDiscrepancyRow, unitSelections, t]);

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('INCOMING_SHIPMENTS_DETAIL_LINKED_CONTRACTS_EMPTY_TITLE'),
      description: t('INCOMING_SHIPMENTS_DETAIL_LINKED_CONTRACTS_EMPTY_DESCRIPTION'),
      primaryLabel: t('ACTION_LINK_CONTRACT'),
      onPrimaryClick: onLinkContract,
    }),
    [t, onLinkContract],
  );

  const summaryTiles = useMemo(() => {
    const summary = shipment.linkedContractsSummary;
    const tile = (key, value, secondary) => ({
      label: t(key, { unit: shipment.declarationUnit }),
      value: [value, secondary].filter(Boolean).join(' '),
    });
    return [
      tile(
        'INCOMING_SHIPMENTS_LINKED_CONTRACTS_SUMMARY_ASSIGNED',
        summary.assignedQuantity,
        summary.assignedQuantitySecondary,
      ),
      tile(
        'INCOMING_SHIPMENTS_LINKED_CONTRACTS_SUMMARY_UNASSIGNED',
        summary.unassignedQuantity,
        summary.unassignedQuantitySecondary,
      ),
    ];
  }, [t, shipment]);

  if (!linkedContracts || linkedContracts.length === 0) {
    return (
      <SummaryTiles>
        <EmptyState
          image={emptyState.image}
          title={emptyState.title}
          description={emptyState.description}
          primaryLabel={emptyState.primaryLabel}
          onPrimaryClick={emptyState.onPrimaryClick}
        />
      </SummaryTiles>
    );
  }

  return (
    <SummaryTiles tiles={summaryTiles}>
      <UnitTypeContext.Provider value={unitTypeContext}>
        <Table
          columns={columns}
          data={linkedContracts}
          getRowId={getLinkedContractId}
          searchable
          labels={tableLabels}
          filters={filterDefinitions}
          onFilterChange={handleFilterChange}
          rowActions={rowActions}
          actionsHeader={t('FIELD_ACTION')}
        />
      </UnitTypeContext.Provider>

      <DiscrepancyModal
        open={contractDiscrepancyRow != null}
        onClose={handleCloseContractDiscrepancy}
        referenceId={contractDiscrepancyRow?.contractId}
        discrepancies={contractDiscrepancies}
        sections={contractDiscrepancySections}
      />

      <LinkShipmentsForContract
        module={module}
        contractDbId={linkShipmentRow?.contractDbId}
        contractLabel={linkShipmentRow?.contractId}
        detailFields={linkShipmentFields}
        open={linkShipmentRow != null}
        onClose={handleCloseLinkShipment}
      />

      <ConfirmDialog
        open={removeLinkRow != null}
        title={t('INCOMING_SHIPMENTS_LINKED_CONTRACTS_ACTION_REMOVE_LINK')}
        bodyTitle={t('INCOMING_SHIPMENTS_LINKED_CONTRACTS_REMOVE_LINK_BODY_TITLE', {
          contractId: removeLinkRow?.contractId,
          shipmentId: shipment.shipmentId,
        })}
        description={t('INCOMING_SHIPMENTS_LINKED_CONTRACTS_REMOVE_LINK_DESCRIPTION')}
        confirmLabel={t('COMMON_REMOVE')}
        tone="danger"
        onConfirm={handleConfirmRemoveLink}
        onCancel={handleCancelRemoveLink}
      />
    </SummaryTiles>
  );
};
